import React, { useEffect, useState } from 'react';
import {
  Paper, Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Checkbox, ListItemText, TextField, CircularProgress, Divider
} from '@mui/material';
import ActionButtons from '../../../commun/ActionButtons';
import EditableField from './EditableField';
import ChipSelectInput from '../../../commun/ChipSelectInput';
import axios from 'axios';
import {
  contractTypeOptions, secteurOptions as secteurOptionsStatic, workEnvironmentOptions
} from '../../../commun/constants/options';
import { parseSecteursExcel } from '../../../commun/autofillExcel';
import Autocomplete from '@mui/material/Autocomplete';
import './detailTalent.css';

const stringToArray = (val) =>
  typeof val === 'string' ? val.split(',').map((s) => s.trim()) : [];

const arrayToString = (arr) =>
  Array.isArray(arr) ? arr.join(', ') : '';

const displayOrUnknown = (value) => {
  if (!value) return 'Non renseigné';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Non renseigné';
  if (typeof value === 'object') return Object.keys(value).length ? JSON.stringify(value) : 'Non renseigné';
  return value.toString().trim() !== '' ? value : 'Non renseigné';
};

const ComplementaireInfos = ({
  editableTalent,
  originalTalent,
  setEditableTalent,
  onSave,
  onFieldChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [secteurOptions, setSecteurOptions] = useState([]);
  const [lieuxOptions, setLieuxOptions] = useState([]);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);

  useEffect(() => {
    parseSecteursExcel().then(setSecteurOptions).catch(() => {
      setSecteurOptions(secteurOptionsStatic);
    });
  }, []);

  const fetchAutocomplete = async (input) => {
    if (!input || input.length < 2) return;
    setLoadingAutocomplete(true);
    try {
      const res = await axios.get('/api/autocomplete', { params: { input } });
      const suggestions = res.data.predictions.map(p => p.description);
      setLieuxOptions(suggestions);
    } catch (err) {
      console.error("Erreur autocomplete :", err);
    } finally {
      setLoadingAutocomplete(false);
    }
  };

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setEditableTalent(originalTalent);
    setIsEditing(false);
  };
  const handleSave = async () => {
    await onSave();
    setIsEditing(false);
  };

  return (
    <Paper elevation={3} className="mission-card-modern">
      <Box className="mission-header">
        <Box className="mission-header-left">
          <Typography variant="h6" className="mission-title">
            Infos complémentaires
          </Typography>
        </Box>
        <Box className="mission-header-right">
          <ActionButtons
            isEditing={isEditing}
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSave={handleSave}
            showView={false}
            showDelete={false}
            showDownload={false}
          />
        </Box>
      </Box>

      <Divider className="mission-divider" />

      <Box>
        <EditableField
          label="Postes recherchés"
          field="postesRecherches"
          isEditing={isEditing}
          value={editableTalent?.postesRecherches?.replace(/"/g, '')}
          onChange={onFieldChange}
        />

        {/* Secteurs */}
        <Box display="flex" gap={2} mb={2}>
          <Box flex={1}>
            <Typography variant="subtitle2" className="label-text">Secteur souhaité</Typography>
            {isEditing ? (
              <ChipSelectInput
                label="Secteur souhaité"
                values={stringToArray(editableTalent?.secteurRecherche).filter(val => val?.trim())}
                onChange={(vals) => onFieldChange('secteurRecherche', arrayToString(vals))}
                options={secteurOptions}
              />
            ) : (
              <Typography>{displayOrUnknown(editableTalent?.secteurRecherche)}</Typography>
            )}
          </Box>

          <Box flex={1}>
            <Typography variant="subtitle2" className="label-text">Secteur non souhaité</Typography>
            {isEditing ? (
              <ChipSelectInput
                label="Secteur non souhaité"
                values={stringToArray(editableTalent?.secteurNonRecherche).filter(val => val?.trim())}
                onChange={(vals) => onFieldChange('secteurNonRecherche', arrayToString(vals))}
                options={secteurOptions}
              />
            ) : (
              <Typography>{displayOrUnknown(editableTalent?.secteurNonRecherche)}</Typography>
            )}
          </Box>
        </Box>

        {/* Types de contrat */}
        <Typography variant="subtitle2" className="label-text">Types de contrat</Typography>
        {isEditing ? (
          <FormControl fullWidth margin="normal">
            <InputLabel shrink>Types de contrat</InputLabel>
            <Select
              multiple
              value={editableTalent.typesContrat || []}
              onChange={(e) => onFieldChange('typesContrat', e.target.value)}
              renderValue={(selected) =>
                selected.map(val => contractTypeOptions.find(opt => opt.value === val)?.label || val).join(', ')
              }
            >
              {contractTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={(editableTalent.typesContrat || []).includes(option.value)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography>
            {displayOrUnknown(
              (editableTalent.typesContrat || []).map(val =>
                contractTypeOptions.find(opt => opt.value === val)?.label || val
              )
            )}
          </Typography>
        )}

        {/* Tarifs */}
        {(editableTalent?.typesContrat || []).map((typeValue) => {
          const label = contractTypeOptions.find(opt => opt.value === typeValue)?.label || typeValue;
          const value = editableTalent?.remunerations?.[typeValue] || '';
          return (
            <div key={typeValue}>
              <Typography variant="subtitle2" className="label-text">Tarif {label}</Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  type="number"
                  value={value}
                  onChange={(e) =>
                    onFieldChange('remunerations', {
                      ...editableTalent.remunerations,
                      [typeValue]: e.target.value
                    })
                  }
                />
              ) : (
                <Typography>
                  {value ? `${value} ${editableTalent.devise?.[0] || ''}` : 'Non renseigné'}
                </Typography>
              )}
            </div>
          );
        })}

        {/* Type de travail */}
        <Typography variant="subtitle2" className="label-text">Type de travail</Typography>
        {isEditing ? (
          <FormControl fullWidth margin="normal">
            <Select
              value={editableTalent?.travailSouhaite || ''}
              onChange={(e) => onFieldChange('travailSouhaite', e.target.value)}
            >
              <MenuItem value="">Non renseigné</MenuItem>
              {workEnvironmentOptions.map((opt) => (
                <MenuItem key={opt.label} value={opt.label}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography>{displayOrUnknown(editableTalent?.travailSouhaite)}</Typography>
        )}

        {/* Lieux souhaités / non souhaités */}
        <Box display="flex" gap={2} mt={3}>
          <Box flex={1}>
            <Typography variant="subtitle2" className="label-text">Lieux souhaités</Typography>
            {isEditing ? (
              <Autocomplete
                multiple
                freeSolo
                options={lieuxOptions}
                loading={loadingAutocomplete}
                value={(editableTalent?.lieuxSouhaites || []).filter(Boolean)}
                onInputChange={(e, input) => fetchAutocomplete(input)}
                onChange={(e, value) => onFieldChange('lieuxSouhaites', value)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Lieux souhaités" />
                )}
              />
            ) : (
              <Typography>{displayOrUnknown(editableTalent?.lieuxSouhaites)}</Typography>
            )}
          </Box>

          <Box flex={1}>
            <Typography variant="subtitle2" className="label-text">Lieux non souhaités</Typography>
            {isEditing ? (
              <Autocomplete
                multiple
                freeSolo
                options={lieuxOptions}
                loading={loadingAutocomplete}
                value={(editableTalent?.lieuxNonSouhaites || []).filter(Boolean)}
                onInputChange={(e, input) => fetchAutocomplete(input)}
                onChange={(e, value) => onFieldChange('lieuxNonSouhaites', value)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Lieux non souhaités" />
                )}
              />
            ) : (
              <Typography>{displayOrUnknown(editableTalent?.lieuxNonSouhaites)}</Typography>
            )}
          </Box>
        </Box>

        {/* État de recherche */}
        <Box mt={3}>
          <Typography variant="subtitle2" className="label-text">Ce talent est-il ?</Typography>
          {isEditing ? (
            <FormControl fullWidth margin="normal">
              <Select
                value={editableTalent?.etatRecherche || ''}
                onChange={(e) => onFieldChange('etatRecherche', e.target.value)}
              >
                <MenuItem value="">Non renseigné</MenuItem>
                <MenuItem value="En recherche active">En recherche active</MenuItem>
                <MenuItem value="À l'écoute d’opportunité">A l'écoute d’opportunité</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Typography>{displayOrUnknown(editableTalent?.etatRecherche)}</Typography>
          )}
        </Box>

        {/* Informations complémentaires */}
        <Box mt={3}>
          <Typography variant="subtitle2" className="label-text">Informations complémentaires</Typography>
          {isEditing ? (
            <TextField
              multiline
              minRows={2}
              fullWidth
              value={editableTalent?.informationsComplementaires || ''}
              onChange={(e) => onFieldChange('informationsComplementaires', e.target.value)}
              placeholder="Ajouter des informations supplémentaires"
            />
          ) : (
            <Typography>{displayOrUnknown(editableTalent?.informationsComplementaires)}</Typography>
          )}
        </Box>
      </Box>

      <Divider className="mission-divider" />
    </Paper>
  );
};

export default ComplementaireInfos;
